import { useCallback } from 'react';
import { selectRequiresWaitingRoomRejoin } from 'state/selectors';
import type { WaitingRoomJoinMode } from '../constants/waitingRoomJoinMode';
import { isHasWaitingListId, isInRoom, resolveWaitingRoomUiState } from '../utils';
import { ConnectToRoomParams, usePerformRoomJoin } from './usePerformRoomJoin';
import { useWaitingRoomPoll } from './useWaitingRoomPoll';

export type { WaitingRoomJoinMode };

type ConnectToRoomResult = { ok: true; waiting?: true } | { ok: false; reason?: 'empty_name' } | undefined;

export interface UseWaitingRoomJoinFlowResult {
    isJoinRejected: boolean;
    isParticipantsLimitRetryLoading: boolean;
    isShowWaitingPage: boolean;
    isNeedsAuth: boolean;
    join: (params?: ConnectToRoomParams) => Promise<ConnectToRoomResult>;
    retryWaitingRoomJoin: () => Promise<ConnectToRoomResult>;
    dismissJoinRejected: () => void;
}

export interface UseWaitingRoomJoinFlowOptions {
    mode: WaitingRoomJoinMode;
    roomName: string;
    getUserName: () => string;
    onEnterRoom: () => void;
    onParticipantsLimitError?: () => void;
    onCallNotStarted?: () => void;
    onUnsupportedP2pJoin?: () => void;
    prepareVideoDevice?: () => Promise<void>;
    hasMessengerAuth?: () => boolean;
    onRequireAuth?: () => void;
}

export const useWaitingRoomJoinFlow = ({
    mode,
    roomName,
    getUserName,
    onEnterRoom,
    onParticipantsLimitError,
    onCallNotStarted,
    onUnsupportedP2pJoin,
    prepareVideoDevice,
    hasMessengerAuth,
    onRequireAuth,
}: UseWaitingRoomJoinFlowOptions): UseWaitingRoomJoinFlowResult => {
    const {
        isWaitingForApproval,
        isJoinRejected,
        isParticipantsLimitRetryLoading,
        connectToRoom: performConnect,
        dismissJoinRejected,
        retryAfterRejection,
    } = usePerformRoomJoin({
        getUserName,
        onEnterRoom,
        onParticipantsLimitError,
        onCallNotStarted,
        onUnsupportedP2pJoin,
        prepareVideoDevice,
    });

    const pollConnect = useCallback(async () => {
        await performConnect({ pollOnly: true });
    }, [performConnect]);

    const hasActiveWaitingQueue = isHasWaitingListId();

    const { isShowWaitingPage, isShouldPoll } = resolveWaitingRoomUiState({
        mode,
        roomName,
        isWaitingForApproval: isWaitingForApproval || hasActiveWaitingQueue,
        isJoinRejected,
        isRequiresWaitingRoomRejoin: selectRequiresWaitingRoomRejoin(),
    });

    useWaitingRoomPoll({
        isWaitingForApproval: isShouldPoll,
        isPaused: isInRoom() || isJoinRejected,
        connectToRoom: pollConnect,
    });

    const isNeedsAuth = Boolean(
        mode === 'embedded' &&
            hasMessengerAuth &&
            onRequireAuth &&
            !hasMessengerAuth() &&
            !hasActiveWaitingQueue &&
            !isJoinRejected,
    );

    const join = useCallback(
        async (params?: ConnectToRoomParams) => {
            if (isNeedsAuth) {
                onRequireAuth?.();
                return undefined;
            }
            return performConnect(params);
        },
        [isNeedsAuth, onRequireAuth, performConnect],
    );

    const retryWaitingRoomJoin = useCallback(async () => retryAfterRejection(), [retryAfterRejection]);

    return {
        isJoinRejected,
        isParticipantsLimitRetryLoading,
        isShowWaitingPage,
        isNeedsAuth,
        join,
        retryWaitingRoomJoin,
        dismissJoinRejected,
    };
};
